import { useState } from 'react';
import {
  Activity,
  Check,
  ContactRound,
  FileText,
  Info,
  KeyRound,
  LayoutDashboard,
  MapPinned,
  MoreHorizontal,
  ReceiptText,
  type LucideIcon,
} from 'lucide-react';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  getOverflowClientTabLabel,
  isOverflowClientTab,
  OVERFLOW_CLIENT_TABS,
  PRIMARY_CLIENT_TABS,
  type ClientTabValue,
} from './clientTabNavModel';

const CLIENT_TAB_ICONS = {
  dashboard: LayoutDashboard,
  orders: ReceiptText,
  docs: FileText,
  contacts: ContactRound,
  location: MapPinned,
  info: Info,
  monitoring: Activity,
  licensing: KeyRound,
} satisfies Record<string, LucideIcon>;

interface ClientTabNavProps {
  activeTab: ClientTabValue;
  onTabChange: (value: ClientTabValue) => void;
}

export function ClientTabNav({ activeTab, onTabChange }: ClientTabNavProps) {
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const overflowActive = isOverflowClientTab(activeTab);
  const overflowLabel = getOverflowClientTabLabel(activeTab);

  const selectOverflowTab = (value: ClientTabValue) => {
    onTabChange(value);
    setSectionsOpen(false);
  };

  return (
    <TabsList className="grid h-12 w-full grid-cols-6">
      {PRIMARY_CLIENT_TABS.map((tab) => {
        const Icon = CLIENT_TAB_ICONS[tab.icon];

        return (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            className="min-w-0 flex-col gap-0.5 px-0.5 py-1 text-[11px] leading-none"
          >
            <Icon className={cn('h-3.5 w-3.5 shrink-0', tab.accentClassName)} />
            <span className="max-w-full truncate">{tab.label}</span>
          </TabsTrigger>
        );
      })}

      <Sheet open={sectionsOpen} onOpenChange={setSectionsOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            data-state={overflowActive ? 'active' : 'inactive'}
            className={cn(
              'inline-flex min-w-0 flex-col items-center justify-center gap-0.5 whitespace-nowrap rounded-sm px-0.5 py-1 text-[11px] font-medium leading-none ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              overflowActive
                ? 'bg-background text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            )}
            aria-label="Еще разделы клиента"
          >
            <MoreHorizontal className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="max-w-full truncate">{overflowLabel}</span>
          </button>
        </SheetTrigger>

        <SheetContent side="bottom" className="max-h-[70dvh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle>Разделы клиента</SheetTitle>
            <SheetDescription className="sr-only">
              Выбор дополнительного раздела карточки клиента.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-2">
            {OVERFLOW_CLIENT_TABS.map((tab) => {
              const selected = activeTab === tab.value;
              const Icon = CLIENT_TAB_ICONS[tab.icon];

              return (
                <button
                  key={tab.value}
                  type="button"
                  className={cn(
                    'flex w-full items-center justify-between rounded-md border px-3 py-3 text-left text-sm transition-colors',
                    selected
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-background text-foreground hover:bg-muted'
                  )}
                  aria-current={selected ? 'page' : undefined}
                  onClick={() => selectOverflowTab(tab.value)}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <Icon className={cn('h-4 w-4 shrink-0', tab.accentClassName)} />
                    <span className="truncate font-medium">{tab.label}</span>
                  </span>
                  {selected && <Check className="h-4 w-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </TabsList>
  );
}
